import React, { useState } from 'react';
import {
    Card,
    CardBody,
    CardHeader,
    Table,
    Badge,
    Spinner,
    Button,
    Row,
    Col
} from 'reactstrap';
import { FiChevronRight, FiDownload } from 'react-icons/fi';
import { useDataFetch } from '../../Hooks/Use-data-fetch';
import { API_BASE_URL } from '../../api/api';
import { usePointValue } from '../../Hooks/use-point-value';
import { formatDate } from '../../utils/formatDate';

const DISPLAY_CURRENCY = 'COP';

const formatCurrency = (value, currency = DISPLAY_CURRENCY) => {
    if (!value) return '$0';

    const symbols = {
        COP: '$',
        USD: '$',
        USDT: '$',
        EUR: '€'
    };

    const symbol = symbols[currency] || '$';

    return `${symbol} ${Number(value).toLocaleString('es-CO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    })}`;
};

const InfluencerTab = ({ user }) => {
    const { getData } = useDataFetch();
    const [showAllGenerators, setShowAllGenerators] = useState(false);
    const pointValue = usePointValue(DISPLAY_CURRENCY);

    const { data, isLoading, error } = getData(
        ['user-influencer-bonus', user?.id, 'week'],
        '/order/admin/calculate/weekly/influencer_bonus',
        { enabled: !!user?.id },
        // El bono influencer se liquida por semana (viernes a jueves).
        { user_id: user?.id, scope: 'week' }
    );

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner color="primary" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-5 text-danger">
                <p>
                    {error.message || 'Error al cargar datos de bono influencer'}
                </p>
            </div>
        );
    }

    const bonusData = data?.data;

    if (!bonusData) {
        return (
            <div className="p-4">
                <div className="text-center py-5 text-muted">
                    <p>
                        No hay datos de bono influencer disponibles para este usuario.
                    </p>
                </div>
            </div>
        );
    }

    const generators = bonusData?.generators || [];
    const levels = bonusData?.levels || [];

    const totalPoints = Number(
        bonusData?.totalInfluencerBonusByLevel?.COP || 0
    );

    const totalOrders = Number(
        bonusData?.orders_count || 0
    );

    const totalSales = generators.reduce((total, generator) => {
        return (
            total +
            (generator.orders || []).reduce((sum, order) => {
                return sum + Number(order.total || 0);
            }, 0)
        );
    }, 0);

    // Lo que genera el bono NO es la suma de las facturas: es lo que valen sus
    // puntos al valor del punto del país (países disponibles).
    const generatedValue = totalPoints * pointValue;

    // `userIds` son todos los directos del usuario; `generators`, los que
    // efectivamente compraron en la semana. La gráfica es esa proporción.
    const totalDirects = (bonusData?.userIds || []).length;

    const buyers = generators.length;

    const buyersPct = totalDirects ? (buyers / totalDirects) * 100 : 0;

    const totalLevelPoints = levels.reduce((sum, level) => {
        return sum + Number(level?.points_by_currency?.COP || 0);
    }, 0);

    const levelBonuses = levels.map((level) => {
        const points = Number(level?.points_by_currency?.COP || 0);

        return {
            level: `Nivel ${level.level}`,
            points,
            users: Number(level.users_count || 0),
            orders: Number(level.orders_count || 0),
            pct: totalLevelPoints
                ? (points / totalLevelPoints) * 100
                : 0
        };
    });

    const visibleGenerators = showAllGenerators
        ? generators
        : generators.slice(0, 6);

    const getGeneratorSales = (generator) => {
        return (generator.orders || []).reduce((sum, order) => {
            return sum + Number(order.total || 0);
        }, 0);
    };

    const getLevelColor = (index) => {
        const colors = [
            '#3b82f6',
            '#8b5cf6',
            '#22c55e',
            '#eab308',
            '#9ca3af'
        ];

        return colors[index % colors.length];
    };

    // Las fechas llegan en hora de Colombia guardada como UTC; formatDate las lee en UTC.
    const periodStart = formatDate(bonusData?.weekPeriod?.start).slice(0, 10);
    const periodEnd = formatDate(bonusData?.weekPeriod?.end).slice(0, 10);

    const HeaderAndSearch = () => (
        <div className="mb-4">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                <div>
                    {periodStart && periodEnd && (
                        <h6 className="fw-bold mb-1">
                            Periodo de corte de bono influencer {periodStart} a {periodEnd}
                        </h6>
                    )}
                    <p className="text-muted mb-0 small">
                        Consulta los bonos generados por cada distribuidor
                    </p>
                </div>
            </div>
        </div>
    );

    const MainStats = () => (
        <Row className="mb-4">
            <Col md={4}>
                <Card className="shadow-sm border-0 h-100">
                    <CardBody className="d-flex align-items-center">
                        <div
                            className="me-3"
                            style={{
                                width: 60,
                                height: 60
                            }}
                        >
                            <img
                                src={`${API_BASE_URL}/users/show/photo/${user?.photo || ''}`}
                                alt={user?.name || 'Usuario'}
                                style={{
                                    width: '60px',
                                    height: '60px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '2px solid #e9ecef'
                                }}
                            />
                        </div>

                        <div>
                            <h5 className="fw-bold mb-1">
                                {user?.name}
                            </h5>

                            <div className="small text-muted mb-1">
                                Código:{' '}
                                <span className="fw-bold">
                                    {user?.multyhealth_code}
                                </span>
                            </div>

                            <Badge
                                color={
                                    user?.network?.influencer_bonus_status
                                        ? 'success'
                                        : 'secondary'
                                }
                                pill
                                className="px-3"
                            >
                                Estado:{' '}
                                {user?.network?.influencer_bonus_status
                                    ? 'Activo'
                                    : 'Inactivo'}
                            </Badge>
                        </div>
                    </CardBody>
                </Card>
            </Col>

            <Col md={4}>
                <Card className="shadow-sm border-0 h-100">
                    <CardBody className="d-flex align-items-center justify-content-between">
                        <div>
                            <div className="text-muted small fw-bold mb-2">
                                Ventas generadas
                            </div>

                            <h3 className="fw-bold mb-0">
                                {formatCurrency(
                                    generatedValue,
                                    DISPLAY_CURRENCY
                                )}
                            </h3>

                            <small className="text-muted">
                                {totalPoints.toLocaleString()} pts x{' '}
                                {formatCurrency(pointValue, DISPLAY_CURRENCY)}{' '}
                                por punto
                            </small>
                        </div>

                        <div className="text-end">
                            <h3 className="fw-bold mb-0">
                                {totalOrders}
                            </h3>

                            <small className="text-muted">
                                Órdenes
                            </small>

                            <div className="small text-primary fw-bold mt-1">
                                {generators.length} generadores
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </Col>

            <Col md={4}>
                <Card className="shadow-sm border-0 h-100">
                    <CardBody>
                        <div className="text-muted small fw-bold mb-3">
                            Resumen del Bono
                        </div>

                        <Row className="align-items-center">
                            <Col xs={5} className="text-center">
                                {/*
                                  La dona repartía los puntos por nivel, pero
                                  el bono de influencer es de un solo nivel:
                                  siempre salía un círculo de un solo color.
                                  Ahora muestra cuántos de sus directos
                                  compraron, que es lo que se quiere leer.
                                */}
                                <div
                                    className="d-flex flex-column align-items-center justify-content-center rounded-circle border mx-auto"
                                    title={`${buyers} de ${totalDirects} directos compraron`}
                                    style={{
                                        width: '100px',
                                        height: '100px',
                                        background: `conic-gradient(${getLevelColor(0)} 0% ${buyersPct}%, #e9ecef ${buyersPct}% 100%)`
                                    }}
                                >
                                    <div
                                        style={{
                                            background: 'white',
                                            width: '70px',
                                            height: '70px',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <span
                                            className="fw-bold"
                                            style={{ fontSize: '14px' }}
                                        >
                                            {buyers}/{totalDirects}
                                        </span>

                                        <span
                                            className="small text-muted"
                                            style={{ fontSize: '10px' }}
                                        >
                                            compraron
                                        </span>
                                    </div>
                                </div>
                            </Col>

                            <Col xs={7}>
                                {levelBonuses.map((level, index) => (
                                    <div
                                        key={level.level}
                                        className="mb-2"
                                    >
                                        <div className="d-flex justify-content-between align-items-center small">
                                            <div className="d-flex align-items-center">
                                                <span
                                                    className="d-inline-block me-2 rounded-circle"
                                                    style={{
                                                        width: '10px',
                                                        height: '10px',
                                                        background:
                                                            getLevelColor(index)
                                                    }}
                                                />

                                                <span>
                                                    {level.level}
                                                </span>
                                            </div>

                                            <span className="text-muted">
                                                {level.pct.toFixed(1)}%
                                            </span>
                                        </div>

                                        <div className="text-muted small mt-1">
                                            {level.users} usuarios ·{' '}
                                            {level.orders} órdenes ·{' '}
                                            {level.points} pts
                                        </div>
                                    </div>
                                ))}
                            </Col>
                        </Row>

                        <div className="text-muted small mt-3 pt-3 border-top">
                            Puntos totales:{' '}
                            <span className="fw-bold text-dark">
                                {totalPoints.toLocaleString()} pts
                            </span>{' '}
                            — el bono de influencer sólo lo generan los
                            directos (nivel 1), no los 7 niveles.
                        </div>
                    </CardBody>
                </Card>
            </Col>
        </Row>
    );

    const DetailsAndTop = () => (
        <Row>
            <Col lg={12}>
                <Card className="shadow-sm border-0 mb-3">
                    <CardHeader className="bg-white border-0 pt-4 px-4 pb-0">
                        <h5 className="fw-bold mb-0">
                            Detalle del Bono
                        </h5>

                        <small className="text-muted">
                            Directos que compraron en la semana y generaron bono para este usuario
                        </small>
                    </CardHeader>

                    <CardBody className="p-0">
                        <div className="table-responsive">
                            <Table hover className="align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th
                                            className="ps-4"
                                            style={{ width: '50px' }}
                                        >
                                            #
                                        </th>

                                        <th>Código</th>

                                        <th>Usuario</th>

                                        <th>Fecha de última compra</th>

                                        <th className="text-center">
                                            Órdenes
                                        </th>

                                        <th className="text-end">
                                            Total ventas
                                        </th>

                                        <th className="text-end pe-4">
                                            Bono
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {visibleGenerators.map(
                                        (generator, index) => {
                                            const generatorSales =
                                                getGeneratorSales(generator);

                                            return (
                                                <tr
                                                    key={generator.user_id}
                                                >
                                                    <td className="ps-4 fw-bold text-muted">
                                                        {index + 1}
                                                    </td>

                                                    <td>
                                                        <Badge
                                                            color="light"
                                                            className="text-dark p-2"
                                                        >
                                                            {
                                                                generator.multyhealth_code
                                                            }
                                                        </Badge>
                                                    </td>

                                                    <td className="fw-bold">
                                                        {generator.name}
                                                    </td>

                                                    <td className="text-muted small">
                                                        {/* orders viene de la más reciente a la más antigua */}
                                                        {formatDate(
                                                            generator.orders?.[0]?.payment_paid_approval ??
                                                            generator.orders?.[0]?.created_at
                                                        ).slice(0, 10) || '-'}
                                                    </td>

                                                    <td className="text-center">
                                                        <Badge
                                                            color="primary"
                                                            pill
                                                        >
                                                            {
                                                                generator.orders_count
                                                            }
                                                        </Badge>
                                                    </td>

                                                    <td className="text-end">
                                                        <span className="fw-bold">
                                                            {formatCurrency(
                                                                generatorSales,
                                                                DISPLAY_CURRENCY
                                                            )}
                                                        </span>
                                                    </td>

                                                    <td className="text-end pe-4">
                                                        <span className="fw-bold text-primary">
                                                            {Number(
                                                                generator.points_total || 0
                                                            ).toLocaleString()}
                                                        </span>

                                                        <span className="text-muted ms-1 small">
                                                            pts
                                                        </span>

                                                        <div className="text-muted small">
                                                            {formatCurrency(
                                                                Number(
                                                                    generator.points_total || 0
                                                                ) * pointValue,
                                                                DISPLAY_CURRENCY
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    )}

                                    <tr className="bg-light fw-bold">
                                        <td
                                            className="ps-4"
                                            colSpan={4}
                                        >
                                            Total
                                        </td>

                                        <td className="text-center">
                                            {totalOrders}
                                        </td>

                                        <td className="text-end">
                                            {formatCurrency(
                                                totalSales,
                                                DISPLAY_CURRENCY
                                            )}
                                        </td>

                                        <td className="text-end pe-4 text-primary">
                                            {totalPoints.toLocaleString()} pts

                                            <div className="text-muted small fw-normal">
                                                {formatCurrency(
                                                    generatedValue,
                                                    DISPLAY_CURRENCY
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </div>

                        {generators.length > 6 && (
                            <div className="text-center py-3 border-top">
                                <Button
                                    color="link"
                                    onClick={() =>
                                        setShowAllGenerators(
                                            !showAllGenerators
                                        )
                                    }
                                    className="text-decoration-none text-primary fw-bold"
                                >
                                    {showAllGenerators
                                        ? 'Ver menos'
                                        : 'Ver todos los generadores'}

                                    <FiChevronRight />
                                </Button>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Col>
        </Row>
    );

    return (
        <div
            className="p-4"
            style={{
                backgroundColor: '#f8f9fa',
                minHeight: '100vh'
            }}
        >
            <HeaderAndSearch />
            <MainStats />
            <DetailsAndTop />
        </div>
    );
};

export default InfluencerTab;